/**
 * What the timeline's metrics row works out from what has been recorded: the scale weight, the
 * trend under it, the days' own step counts, and every measurement that has been taken. Values
 * stay in the units they are kept in — kilograms, metres, a ratio, a plain count — so a card
 * formats once, at the end, and the curve is the same shape either way.
 */

import { MEASUREMENTS, SLEEP_MEASUREMENTS, type WeighIn, type Measurement } from '~/data/weighins'
import type { Expenditure } from '~/data/expenditure'
import type { DailyMetric } from '~/data/metrics'
import { TARGETED, loggedDays, type NutritionData } from '~/data/nutrition'
import type { NutritionKind } from '~/lib/metric-text'
import { dayKey, dayStart } from '~/lib/dates'
import type { DayValue, Domain } from '~/lib/day-value'

/**
 * How far a weigh-in moves the trend toward itself. A tenth is the Hacker's Diet's figure, and
 * it is the whole point of the second card: a heavy Sunday bends the line rather than yanking
 * it, so what is left is the direction the week actually went.
 */
const TREND_SHARE = 0.1

/** The span the trend's movement is read over — long enough that a wobble is not a trend. */
const FORTNIGHT = 14

/**
 * The span a day's steps are read against. A day's walking swings so far that the difference
 * from yesterday says nothing at all — a rest day after a hike reads as a collapse — so the
 * steps card is measured against the week it sits at the end of instead.
 */
const STEPS_WEEK = 7

/**
 * How long a silence has to run before the line across it is drawn as the guess it is, as a
 * share of the stretch being looked at. A day unlogged is a gap on a week's chart and nothing
 * at all on a year's, so the question is always "a gap compared to what" — and the answer is
 * the stretch.
 */
const GAP_SHARE = 20

/** That share as a number of days, never less than one: a day is the smallest silence there is. */
export function gapDaysFor(domain: Domain): number {
  return Math.max(1, (domain.to - domain.from) / 86_400_000 / GAP_SHARE)
}

/**
 * What one card is about: the scale, the trend under it, the day's steps, a measurement, or
 * one of the diary's — a day's intake, the expenditure.
 */
/**
 * A reading with a card and a chart of its own. Every measurement but the two that make up a
 * night: how you slept is read as a length, and a length worked out from two clock times is
 * not a series either of them can be charted as — see `sleepMinutes`.
 */
export type ChartedMeasurement = Exclude<Measurement, (typeof SLEEP_MEASUREMENTS)[number]>

export const CHARTED_MEASUREMENTS = MEASUREMENTS.filter(
  (name): name is ChartedMeasurement => !(SLEEP_MEASUREMENTS as readonly string[]).includes(name),
)

export type MetricCardKind = 'scale' | 'trend' | 'steps' | ChartedMeasurement | NutritionKind

/** Whether a card's readings are drawn as days standing on the floor rather than as a line. */
export const isCounted = (kind: MetricCardKind) =>
  kind === 'steps' ||
  kind === 'calories' ||
  kind === 'protein' ||
  kind === 'carbs' ||
  kind === 'fat'

export interface MetricCard {
  readonly kind: MetricCardKind
  /** Oldest first, as the chart draws them. */
  readonly points: readonly DayValue[]
  /** The latest reading, which is the number the card is about. */
  readonly latest: number
  /**
   * How far it has moved, in the same unit as the points. Null where there is nothing to
   * measure against: a single reading, or a trend with no weigh-in a fortnight old.
   */
  readonly change: number | null
  /** The days graphed as what they hold but marked as not the whole of it. */
  readonly incomplete?: readonly string[]
  /**
   * A value ruled across the chart — the goal's target weight, on the trend card, where the
   * goal in force names one. The chart's band reaches it, so the rule is always on the chart.
   */
  readonly rule?: number
}

/**
 * The smoothed line under the weigh-ins. The first is where it starts — with one reading there
 * is nothing yet to smooth — and every one after moves it a share of the way toward itself.
 */
export function trendOf(points: readonly DayValue[]): DayValue[] {
  let held: number | null = null
  return points.map((point) => {
    held = held === null ? point.value : held + TREND_SHARE * (point.value - held)
    return { day: point.day, value: held }
  })
}

/** The day a given number of days before this one, stepped by the calendar so a clock change
 * does not land it on the wrong side of midnight. */
export function daysBefore(day: string, days: number): string {
  const at = new Date(dayStart(day))
  at.setDate(at.getDate() - days)
  return dayKey(at.getTime())
}

/**
 * The first day of a stretch of `days` days ending on `to` — the weigh-in spec, The metrics
 * page. A stretch is as many days as it names, the day it ends on the last of them: a week is
 * that day and the six before it, not seven days back from it, which is eight days drawn.
 */
export function stretchFrom(to: string, days: number): string {
  return daysBefore(to, days - 1)
}

/** How far the latest reading is from the one before it. */
function changeSince(points: readonly DayValue[]): number | null {
  const latest = points[points.length - 1]
  const before = points[points.length - 2]
  return latest === undefined || before === undefined ? null : latest.value - before.value
}

/**
 * How far the trend has moved over the last fortnight, read against the latest weigh-in that
 * old. A history that does not reach back that far says nothing rather than calling however
 * long it has been a fortnight.
 */
function fortnightChange(trend: readonly DayValue[], today: string): number | null {
  const edge = daysBefore(today, FORTNIGHT)
  const latest = trend[trend.length - 1]
  const back = [...trend].reverse().find((point) => point.day <= edge)
  return latest === undefined || back === undefined || back === latest
    ? null
    : latest.value - back.value
}

/**
 * How far the latest day stands from the average of the week up to it — the reading the steps
 * card names in place of a difference from the day before.
 *
 * The average is of the days in that week that were actually counted, the latest among them:
 * a week half of which the phone spent on a desk is a week of the days it was carried, not a
 * week of zeroes that would drag the average to the floor.
 */
function weekChange(points: readonly DayValue[]): number | null {
  const latest = points[points.length - 1]
  if (latest === undefined) {
    return null
  }
  const edge = daysBefore(latest.day, STEPS_WEEK)
  const week = points.filter((point) => point.day > edge)
  if (week.length < 2) {
    return null
  }
  const mean = week.reduce((sum, point) => sum + point.value, 0) / week.length
  return latest.value - mean
}

/** A card, given everything but the reckoning of what it holds. */
function cardOf(
  kind: MetricCardKind,
  points: DayValue[],
  change: number | null,
): MetricCard | null {
  const latest = points[points.length - 1]
  return latest === undefined ? null : { kind, points, latest: latest.value, change }
}

/**
 * The cards the metrics row holds: the two weight cards where a weight has ever been recorded,
 * the steps card where any day has been counted, then one for each measurement that has a
 * reading, in the order the weigh-in offers them.
 */
export function metricCards(
  weighIns: readonly WeighIn[],
  metrics: readonly DailyMetric[],
  today: string,
  nutrition: NutritionData | null = null,
  /** The expenditure as worked out from the above and the stats; null draws no card of it. */
  expenditure: Expenditure | null = null,
): MetricCard[] {
  // The store holds them newest first, and a graph is read the way a life was lived.
  const days = [...weighIns].sort((a, b) => (a.day < b.day ? -1 : 1))

  const weights = days.flatMap((weighIn) =>
    weighIn.weightKg === null ? [] : [{ day: weighIn.day, value: weighIn.weightKg }],
  )
  const trend = trendOf(weights)
  const steps = metrics
    .filter((metric) => metric.kind === 'steps')
    .map((metric) => ({ day: metric.day, value: metric.value }))
    .sort((a, b) => (a.day < b.day ? -1 : 1))

  /*
   * The trend is read against the goal's target, where the goal in force today names one: the
   * latest begun by today and not ended by it — the same goal the Android card rules at.
   */
  const inForce = nutrition?.goals.filter((goal) => goal.effectiveFrom <= today).at(-1)
  const target =
    inForce !== undefined && (inForce.endedOn === null || today < inForce.endedOn)
      ? inForce.targetWeightKg
      : null
  const trendCard = cardOf('trend', trend, fortnightChange(trend, today))
  const cards = [
    cardOf('scale', weights, changeSince(weights)),
    trendCard === null || target === null ? trendCard : { ...trendCard, rule: target },
    cardOf('steps', steps, weekChange(steps)),
    // Every measurement but the two that make a night — see `ChartedMeasurement`.
    ...CHARTED_MEASUREMENTS.map((name) => {
      const points = days.flatMap((weighIn) => {
        const read = weighIn.measurements[name]
        return read === null ? [] : [{ day: weighIn.day, value: read }]
      })
      return cardOf(name, points, changeSince(points))
    }),
    ...(nutrition === null ? [] : nutritionCards(nutrition, expenditure, today)),
  ]
  return cards.filter((card) => card !== null)
}

/**
 * The diary's cards, once any day holds an entry: calories and each macro as days standing
 * on the floor, and the expenditure as a line. An incomplete day is graphed as what it holds
 * and marked so.
 */
function nutritionCards(
  nutrition: NutritionData,
  expenditure: Expenditure | null,
  today: string,
): (MetricCard | null)[] {
  const days = loggedDays(nutrition)
  if (days.length === 0) {
    return []
  }
  const totals = nutrition.days.map((each) => ({ day: each.day, total: each.nutrients }))
  const incomplete = nutrition.incompleteDays.filter((day) => days.includes(day))
  const intake = TARGETED.map((name) => {
    const points = totals.flatMap(({ day, total }) => {
      const value = total[name]
      return value === null ? [] : [{ day, value }]
    })
    const card = cardOf(name, points, weekChange(points))
    return card === null ? null : { ...card, incomplete }
  })
  const spent = expenditure?.series.map((point) => ({ day: point.day, value: point.value })) ?? []
  return [...intake, cardOf('expenditure', spent, fortnightChange(spent, today))]
}

/** How much of the past a weight chip carries behind it. */
const CHIP_WEEK = 7

/**
 * What a weight chip is about: the two weights it can name, and the week of each it draws
 * behind them.
 *
 * The week ends on the day the chip is about rather than today — a set from a year ago is
 * graphed against the week it was performed in, which is the only week that says anything
 * about it.
 *
 * The trend is worked out over every weigh-in up to that day and only then cut to the week.
 * It is a running figure: reckoned from the week alone it would start afresh at whatever
 * Monday happened to say, which is not the trend at all.
 */
export interface WeightWeek {
  /** Oldest first, as the chart draws them. Empty where nothing was weighed in that week. */
  readonly scale: readonly DayValue[]
  readonly trend: readonly DayValue[]
  /** What the chip names: the latest weigh-in on or before the day, and the trend there. */
  readonly scaleNow: number | null
  readonly trendNow: number | null
  /** The day [scaleNow] was weighed on, which need not be the day of the latest weigh-in. */
  readonly scaleOn: string | null
}

export function weightWeek(weighIns: readonly WeighIn[], day: string): WeightWeek {
  const upTo = [...weighIns]
    .filter((weighIn) => weighIn.day <= day)
    .sort((a, b) => (a.day < b.day ? -1 : 1))
  const weights = upTo.flatMap((weighIn) =>
    weighIn.weightKg === null ? [] : [{ day: weighIn.day, value: weighIn.weightKg }],
  )
  const trend = trendOf(weights)

  const from = stretchFrom(day, CHIP_WEEK)
  const inWeek = (point: DayValue) => point.day >= from
  const last = <T>(held: readonly T[]): T | null => held[held.length - 1] ?? null

  return {
    scale: weights.filter(inWeek),
    trend: trend.filter(inWeek),
    scaleNow: last(weights)?.value ?? null,
    trendNow: last(trend)?.value ?? null,
    scaleOn: last(weights)?.day ?? null,
  }
}
